import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { logError, logInfo } from '../utils/logging';
import { sttApi } from '../api/sttApi'; // Import stt client

function CreateKey() {
  const navigate = useNavigate();

  
  const [keyName, setKeyName] = useState('');
  const [loading, setLoading] = useState(false); // Local loading state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!keyName.trim()) {
      setError('Please enter a name for your API key');
      return;
    }

    setLoading(true); // Start local loading
    setError('');

    try {
      logInfo('CreateKey: Making API request to create key');
      
      const data = await sttApi.createApiKey(keyName); // Use stt client function
      
      setNewApiKey(data.key);
      setShowKeyDialog(true);
      setSuccess('API key created successfully!');
      
    } catch (err) {
      logError(err, { context: 'CreateKey handleSubmit' });
      setError(err.message);
    } finally {
      setLoading(false); // End local loading
    }
  };

  const handleCloseDialog = () => {
    setShowKeyDialog(false);
    setNewApiKey('');
    setKeyName('');
    // Dispatch auth change event to update dashboard
    window.dispatchEvent(new Event('authChange'));
    navigate('/dashboard');
  };

  const handleCopyKey = async () => {
    setCopySuccess('');

    const textToCopy = newApiKey || '';
    if (!textToCopy) {
      setCopySuccess('Nothing to copy');
      return;
    }

    // 1) Modern clipboard API (works in HTTPS / localhost)
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        setCopySuccess('Copied!');
        return;
      }
    } catch (err) {
      // Continue to fallback
      logError(err, { context: 'CreateKey handleCopyKey navigator.clipboard' });
    }

    // 2) Fallback (more compatible)
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;

      // Prevent scrolling to bottom on iOS / keep off-screen
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      textArea.style.opacity = '0';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length); // important for mobile

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!successful) {
        throw new Error('execCommand(copy) returned false');
      }

      setCopySuccess('Copied!');
    } catch (err) {
      logError(err, { context: 'CreateKey handleCopyKey fallback execCommand' });
      setCopySuccess('Failed to copy!');
    }
  };


  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create New API Key
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }} align="center">
          Create a new API key to access the STT service. Give it a descriptive name to help you identify it later.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-test-id="create-key-error">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} data-test-id="create-key-success">
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} data-test-id="create-key-form">
          <TextField
            fullWidth
            label="API Key Name"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            placeholder="e.g., Production Key, Test Key, etc."
            helperText="Choose a descriptive name for your API key"
            inputProps={{ 'data-test-id': 'create-key-name-input' }}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !keyName.trim()}
              fullWidth
              data-test-id="create-key-submit"
            >
              {loading ? 'Creating...' : 'Create API Key'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              fullWidth
              data-test-id="create-key-cancel"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* API Key Display Dialog */}
      <Dialog
        open={showKeyDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        data-test-id="create-key-dialog"
      >
        <DialogTitle>API Key Created Successfully!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your new API key has been created. Please copy and save it securely - you won't be able to see it again!
          </Typography>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'
          }}>
            <Typography
              component="pre"
              data-test-id="create-key-value"
              sx={{
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                m: 0
              }}
            >
              {showApiKey ? newApiKey : '********************************'}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            ⚠️ Important: Copy this key now. It will not be shown again for security reasons.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,  marginRight: 'auto' }}>
            {copySuccess && <Typography color="success.main">{copySuccess}</Typography>}
            <Tooltip title={showApiKey ? 'Hide' : 'Show'}>
              <IconButton onClick={toggleApiKeyVisibility} data-test-id="create-key-toggle-visibility">
                {showApiKey ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyKey} data-test-id="create-key-copy">
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>
          <Button onClick={handleCloseDialog} variant="outlined" data-test-id="create-key-done">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CreateKey;